import type {
  CatalogExporter,
  CatalogExportSettingsColumn,
} from '@backstage/plugin-catalog';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * The two helpers below mirror `serializeEntities.ts` of
 * `@backstage/plugin-catalog`, which the built-in CSV and JSON exporters use
 * but which the package does not export. Keeping them identical is what makes a
 * YAML export line up column for column with a CSV one of the same selection.
 */
const getColumnTitle = (column: CatalogExportSettingsColumn) =>
  column.title ?? column.entityFilterKey;

const getByPath = (obj: unknown, path: string): unknown =>
  path
    .split('.')
    .reduce<unknown>(
      (acc, part) =>
        acc === null || acc === undefined
          ? undefined
          : (acc as Record<string, unknown>)[part],
      obj,
    );

/**
 * Adds a YAML format to the catalog export dialog.
 *
 * Like the built-in exporters it streams: `streamEntities` pages through the
 * whole filtered result set (500 entities at a time by default) and each page
 * is serialised and pushed to the download as it arrives, so exporting the full
 * catalog never holds it in memory.
 *
 * The output is one YAML sequence, not one document per entity as the upstream
 * example sketches. What the dialog exports is a projection over the selected
 * columns — records, not entity descriptors — and a sequence is how a list of
 * records is written in YAML. It also stays streamable: `stringify([row])`
 * emits exactly one correctly indented sequence item, so the pages simply
 * concatenate. Switching to `catalog-info.yaml`-shaped documents would mean
 * ignoring the column checkboxes the dialog shows.
 *
 * Absent fields are written as `null` rather than dropped, so every record has
 * the same keys — `yaml` omits `undefined` values by default, which would
 * otherwise produce ragged records.
 *
 * `yaml` is imported inside the generator: the blueprint takes the exporter as
 * a plain function with no lazy loader of its own, so a static import would put
 * the library in the app's main chunk for a dialog most sessions never open.
 */
export const yamlExporter: CatalogExporter = ({
  apis,
  columns,
  streamRequest,
}) => {
  const catalogApi = apis.get(catalogApiRef);
  if (!catalogApi) {
    throw new Error('Cannot export: the catalog API is not available');
  }

  async function* generateYaml() {
    const { stringify } = await import('yaml');

    let wroteAnything = false;
    for await (const page of catalogApi!.streamEntities(streamRequest)) {
      for (const entity of page) {
        const record: Record<string, unknown> = {};
        for (const column of columns) {
          record[getColumnTitle(column)] =
            getByPath(entity, column.entityFilterKey) ?? null;
        }
        // `lineWidth: 0` disables folding, so a long description stays on one
        // line instead of being wrapped across several.
        yield stringify([record], { lineWidth: 0 });
        wroteAnything = true;
      }
    }

    if (!wroteAnything) {
      yield '[]\n';
    }
  }

  return {
    generator: generateYaml(),
    // `application/yaml` is the media type registered by RFC 9512; the upstream
    // example predates it and still writes `application/x-yaml`.
    contentType: 'application/yaml',
  };
};
